import { useCallback, useEffect, useRef, useState } from "react";
import { useSalonId } from "@/lib/store/salon";
import {
  OpenAIWhisperSTTAdapter,
  type RecordingStatus,
} from "../adapters/openai-whisper-stt-adapter";

interface UseVoiceRecordingOptions {
  onTranscriptComplete?: (transcript: string) => void;
  onError?: (error: Error) => void;
}

interface UseVoiceRecordingReturn {
  isRecording: boolean;
  isProcessing: boolean;
  error: Error | null;
  duration: number;
  isSupported: boolean;
  hasPermission: boolean | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  cancelRecording: () => void;
  clearError: () => void;
}

/**
 * Custom hook for managing voice recording with OpenAI Whisper
 *
 * Provides state management and control functions for recording audio,
 * transcribing it, and handling errors.
 */
export function useVoiceRecording(options: UseVoiceRecordingOptions = {}): UseVoiceRecordingReturn {
  const { onTranscriptComplete, onError } = options;
  const salonId = useSalonId();

  const [status, setStatus] = useState<RecordingStatus>({
    state: "idle",
    duration: 0,
  });

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const adapterRef = useRef<OpenAIWhisperSTTAdapter | null>(null);

  // Check if browser supports required APIs
  const isSupported =
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices !== "undefined" &&
    typeof MediaRecorder !== "undefined";

  // Initialize adapter
  useEffect(() => {
    if (isSupported && salonId) {
      adapterRef.current = new OpenAIWhisperSTTAdapter({ salonId });
    }

    return () => {
      // Cleanup on unmount
      if (adapterRef.current && status.state === "recording") {
        adapterRef.current.cancelRecording();
      }
    };
  }, [isSupported, salonId, status.state]);

  // Subscribe to adapter status changes
  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;

    const unsubscribe = adapter.subscribe(() => {
      setStatus({ ...adapter.status });
    });

    return unsubscribe;
  }, []);

  // Check microphone permission status
  useEffect(() => {
    if (!isSupported) {
      setHasPermission(false);
      return;
    }

    // Check if Permissions API is available
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "microphone" as PermissionName })
        .then((result) => {
          setHasPermission(result.state === "granted");

          // Listen for permission changes
          result.addEventListener("change", () => {
            setHasPermission(result.state === "granted");
          });
        })
        .catch(() => {
          // Permissions API not fully supported, will check on first use
          setHasPermission(null);
        });
    }
  }, [isSupported]);

  // Notify parent of errors
  useEffect(() => {
    if (status.error && onError) {
      onError(status.error);
    }
  }, [status.error, onError]);

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    const adapter = adapterRef.current;
    if (!adapter) {
      throw new Error("Voice recording not supported");
    }

    try {
      await adapter.startRecording();
      setHasPermission(true);
    } catch (error) {
      console.error("[useVoiceRecording] Failed to start recording:", error);

      // Update permission status if it was a permission error
      if (error instanceof Error) {
        if (error.message.includes("permission") || error.message.includes("denied")) {
          setHasPermission(false);
        }
      }

      throw error;
    }
  }, []);

  /**
   * Stop recording and get transcript
   */
  const stopRecording = useCallback(async () => {
    const adapter = adapterRef.current;
    if (!adapter) {
      throw new Error("Voice recording not supported");
    }

    try {
      const transcript = await adapter.stopRecording();

      if (onTranscriptComplete) {
        onTranscriptComplete(transcript);
      }
    } catch (error) {
      console.error("[useVoiceRecording] Failed to stop recording:", error);
      throw error;
    }
  }, [onTranscriptComplete]);

  /**
   * Cancel recording without transcription
   */
  const cancelRecording = useCallback(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;

    adapter.cancelRecording();
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setStatus((prev) => ({
      ...prev,
      error: undefined,
    }));
  }, []);

  return {
    isRecording: status.state === "recording",
    isProcessing: status.state === "processing",
    error: status.error || null,
    duration: status.duration,
    isSupported,
    hasPermission,
    startRecording,
    stopRecording,
    cancelRecording,
    clearError,
  };
}
